import type { PermissionKey } from "@/constants/permissions"
import { PERMISSIONS } from "@/constants/permissions"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import type { FeatureFlagMap } from "@/types/featureFlag"

export type CustomerGroup = "G1" | "G2" | "G3"

export type UpsellProduct = "mc" | "questionnaire"

export type SidebarIconKey =
  | "upload"
  | "history"
  | "database"
  | "autoPilots"
  | "folder"

export type SidebarEntry =
  | {
      kind: "item"
      id: string
      labelKey: TranslationKeys
      path: string
      iconKey: SidebarIconKey
      /** VIEW-permission gate. Omit for entries that anyone should see. */
      requiresPermission?: PermissionKey
    }
  | { kind: "lockedMc"; id: "navItemMaterialCompliance" }
  | { kind: "lockedQuestionnaire"; id: "navItemQuestionnaireLocked" }
  | {
      kind: "mcGroup"
      id: "navItemMaterialCompliance"
      /** Sub-item permission filtering happens inside MaterialComplianceSidebarGroup. */
    }
  | {
      kind: "chat"
      id: "navItem4"
      requiresPermission?: PermissionKey
    }
  | { kind: "companyInfo"; id: "navItem7" }
  | { kind: "divider"; id: string }
  | {
      kind: "lockedOnboarding"
      id: string
      labelKey: TranslationKeys
      iconKey: SidebarIconKey
    }

export interface SidebarNavModel {
  group: CustomerGroup
  entries: SidebarEntry[]
  upsell: UpsellProduct | null
  upsellCard: "mc" | null
}

const UPLOAD: SidebarEntry = {
  kind: "item",
  id: "navItem1",
  labelKey: "SIDEBAR/NEW_QUESTIONNAIRE",
  path: "/questionnaire/upload",
  iconKey: "upload",
  requiresPermission: PERMISSIONS.QUEST_VIEW,
}
const HISTORY: SidebarEntry = {
  kind: "item",
  id: "navItem2",
  labelKey: "SIDEBAR/HISTORY",
  path: "/history",
  iconKey: "history",
  requiresPermission: PERMISSIONS.QUEST_VIEW,
}
const CHAT: SidebarEntry = {
  kind: "chat",
  id: "navItem4",
  requiresPermission: PERMISSIONS.CHAT_USE,
}
const DATABASE: SidebarEntry = {
  kind: "item",
  id: "navItem3",
  labelKey: "SIDEBAR/COMPANY_DATA",
  path: "/knowledgebase",
  iconKey: "database",
  requiresPermission: PERMISSIONS.KB_VIEW,
}
const AUTO_PILOTS: SidebarEntry = {
  kind: "item",
  id: "navItem5",
  labelKey: "SIDEBAR/AUTO_PILOTS",
  path: "/qms-database",
  iconKey: "autoPilots",
}
const COMPANY_INFO: SidebarEntry = { kind: "companyInfo", id: "navItem7" }
const MC_GROUP: SidebarEntry = {
  kind: "mcGroup",
  id: "navItemMaterialCompliance",
}
const MC_LOCKED: SidebarEntry = {
  kind: "lockedMc",
  id: "navItemMaterialCompliance",
}
const QUESTIONNAIRE_LOCKED: SidebarEntry = {
  kind: "lockedQuestionnaire",
  id: "navItemQuestionnaireLocked",
}

export const deriveCustomerGroup = (flags: FeatureFlagMap): CustomerGroup => {
  if (!flags.MATERIAL_COMPLIANCE) return "G1"
  return flags.SIDEBAR_MC_ONLY ? "G2" : "G3"
}

// G2 only exists under the V2 sidebar — the legacy sidebar has no MC-only
// layout, so SIDEBAR_V2 must also be on for the group to apply.
export const isMcOnlyGroup = (flags: FeatureFlagMap): boolean =>
  flags.SIDEBAR_V2 && deriveCustomerGroup(flags) === "G2"

const DATA_ROOM: SidebarEntry = {
  kind: "item",
  id: "navItemDataRoom",
  labelKey: "SIDEBAR/DATA_ROOM",
  path: "/data-room",
  iconKey: "folder",
}

export const resolveSidebarNav = (flags: FeatureFlagMap): SidebarNavModel => {
  // Onboarding-exclusive mode: the data room is the only live surface; every
  // other module renders locked with "unlocked after onboarding" messaging.
  if (flags.ONBOARDING_DATA_ROOM) {
    return {
      group: deriveCustomerGroup(flags),
      upsell: null,
      upsellCard: null,
      entries: [
        DATA_ROOM,
        { kind: "divider", id: "divider-onboarding" },
        {
          kind: "lockedOnboarding",
          id: "lockedUpload",
          labelKey: "SIDEBAR/NEW_QUESTIONNAIRE",
          iconKey: "upload",
        },
        {
          kind: "lockedOnboarding",
          id: "lockedHistory",
          labelKey: "SIDEBAR/HISTORY",
          iconKey: "history",
        },
        {
          kind: "lockedOnboarding",
          id: "lockedDatabase",
          labelKey: "SIDEBAR/COMPANY_DATA",
          iconKey: "database",
        },
      ],
    }
  }

  const group = deriveCustomerGroup(flags)

  const standard: SidebarEntry[] = [
    UPLOAD,
    HISTORY,
    CHAT,
    DATABASE,
    ...(flags.COMPANY_PROFILE ? [COMPANY_INFO] : []),
  ]
  const autoPilots: SidebarEntry[] = flags.AUTO_PILOTS_INTERNAL
    ? [AUTO_PILOTS]
    : []

  if (group === "G1") {
    return {
      group,
      upsell: "mc",
      upsellCard: "mc",
      entries: [
        ...standard,
        { kind: "divider", id: "divider-mc" },
        MC_LOCKED,
        ...autoPilots,
      ],
    }
  }

  if (group === "G2") {
    return {
      group,
      upsell: "questionnaire",
      upsellCard: null,
      entries: [
        MC_GROUP,
        ...autoPilots,
        { kind: "divider", id: "divider-standard" },
        CHAT,
        DATABASE,
        ...(flags.COMPANY_PROFILE ? [COMPANY_INFO] : []),
        { kind: "divider", id: "divider-questionnaire" },
        QUESTIONNAIRE_LOCKED,
      ],
    }
  }

  return {
    group,
    upsell: null,
    upsellCard: null,
    entries: [
      ...standard,
      { kind: "divider", id: "divider-mc" },
      MC_GROUP,
      ...autoPilots,
    ],
  }
}
